import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, X } from "lucide-react";
import { Button } from "./ui/button";
import { setupAPI } from "../lib/api";
import {
  clearSetupTourState,
  dispatchSetupTourStatusChanged,
  getNextSetupTourStepId,
  getSetupTourStep,
  readSetupTourState,
  resolveSetupTourText,
  SETUP_TOUR_STEP_IDS,
  updateSetupTourState,
} from "../lib/setupTour";
import { toast } from "sonner";
import { useVocab } from "../context/TerminologyContext";

const STEP_ORDER = SETUP_TOUR_STEP_IDS;

const STEP_CONFIG = {
  profile: {
    focusGroups: [
      ["[data-tour='setup-nav-settings']"],
      [
        "[data-tour='setup-company-tab']",
        "[data-tour='setup-company-section']",
        "[data-tour='setup-company-terminology']",
        "[data-tour='setup-company-billing']",
      ],
    ],
    actionScrollGroup: 1,
    actionSelector: "[data-tour='setup-company-save']",
    primaryLabel: "Speichern",
    hint:
      "Trage hier Firmenname, Unternehmens-E-Mail und vollständige Rechnungsadresse ein und wähle deine Betriebsart. Prüfe auch die Steuerangaben, bevor du Rechnungen ausstellst. Fehlt etwas, kannst du den Schritt später fortsetzen oder bewusst überspringen.",
  },
  studio: {
    focusGroups: [
      ["[data-tour='setup-nav-locations']"],
      [
        "[data-tour='setup-location-tab-details']",
        "[data-tour='setup-location-details']",
        "[data-tour='setup-location-create']",
      ],
    ],
    actionScrollGroup: 1,
    actionSelector: ["[data-tour='setup-location-save']", "[data-tour='setup-location-create']"],
    primaryLabel: "Standort einrichten",
    hint:
    (vocab) => `Lege jetzt deinen Standort an oder speichere die Standortdaten. Danach führen wir dich direkt zu deinen ${vocab.service.pl}.`,
  },
  services: {
    focusGroups: [
      ["[data-tour='setup-nav-services']"],
      ["[data-tour='setup-service-create']", "[data-tour='setup-service-dialog']"],
    ],
    actionScrollGroup: 1,
    actionSelector: ["[data-tour='setup-service-save']", "[data-tour='setup-service-create']"],
    primaryLabel: (vocab) => `${vocab.service.sg} anlegen`,
    hint: (vocab) =>
      `Hier definierst du, was ${vocab.person.pl} buchen können. Der Primärbutton nutzt immer die echte Aktion in diesem Bereich.`,
  },
  payments: {
    focusGroups: [
      ["[data-tour='setup-nav-settings']"],
      ["[data-tour='setup-payments-tab']", "[data-tour='setup-payments-card']"],
    ],
    actionScrollGroup: 1,
    actionSelector: "[data-tour='setup-payments-action']",
    primaryLabel: "Zahlungen einrichten",
    hint:
      "Stripe prüft einige Angaben selbst. Nach der Rückkehr kannst du die Einrichtung hier abschließen – auch ohne Online-Zahlungen.",
  },
  booking: {
    focusGroups: [["[data-tour='setup-booking-link']"]],
    actionScrollGroup: 0,
    actionSelector: "[data-tour='setup-booking-link']",
    primaryLabel: "Buchungsseite öffnen",
    hint:
    (vocab) => `Prüfe deine Buchungsseite so, wie ${vocab.person.pl} sie sehen. Danach folgt nur noch der optionale Zahlungsschritt.`,
  },
};

function queryFirst(selectors) {
  if (!selectors) return null;
  const selectorList = Array.isArray(selectors) ? selectors : [selectors];
  for (const selector of selectorList) {
    const node = document.querySelector(selector);
    if (node) return node;
  }
  return null;
}

function queryAll(selectors) {
  if (!Array.isArray(selectors)) return [];
  const nodes = [];
  for (const selector of selectors) {
    const matches = Array.from(document.querySelectorAll(selector));
    for (const node of matches) {
      if (node && !nodes.includes(node)) {
        nodes.push(node);
      }
    }
  }
  return nodes;
}

function buildGroupRect(nodes) {
  const rects = (nodes || [])
    .map((node) => node?.getBoundingClientRect?.())
    .filter((rect) => rect && rect.width > 0 && rect.height > 0);
  if (!rects.length) return null;

  const top = Math.min(...rects.map((rect) => rect.top));
  const left = Math.min(...rects.map((rect) => rect.left));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return {
    top: Math.max(8, top - 12),
    left: Math.max(8, left - 12),
    width: Math.max(0, right - left + 24),
    height: Math.max(0, bottom - top + 24),
  };
}

function triggerAction(node) {
  if (!node) return false;
  try {
    node.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  } catch {
    // ignore
  }

  try {
    if (typeof node.click === "function") {
      node.click();
      return true;
    }
  } catch {
    // ignore
  }

  try {
    const form = node.closest("form");
    if (form) {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else if (typeof form.submit === "function") {
        form.submit();
      }
      return true;
    }
  } catch {
    // ignore
  }

  return false;
}

function resolveFocusGroups(stepId, stepConfig) {
  if (stepId === "services") {
    const dialogVisible = Boolean(queryFirst("[data-tour='setup-service-dialog']"));
    return dialogVisible
      ? [
          ["[data-tour='setup-nav-services']"],
          ["[data-tour='setup-service-dialog']"],
        ]
      : [
          ["[data-tour='setup-nav-services']"],
          ["[data-tour='setup-service-create']"],
        ];
  }

  return stepConfig?.focusGroups || [];
}

export default function SetupTourOverlay() {
  const vocab = useVocab();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [focusRects, setFocusRects] = useState([]);
  const [actionPending, setActionPending] = useState(false);
  const [saving, setSaving] = useState(false);

  const setupEnabled = searchParams.get("setup") === "1";
  const stepId = searchParams.get("setupStep") || "";
  const step = getSetupTourStep(stepId);
  const stepIndex = step ? STEP_ORDER.indexOf(step.id) : -1;
  const stepConfig = step ? STEP_CONFIG[step.id] : null;

  useEffect(() => {
    if (!setupEnabled || !stepConfig || !step) {
      setFocusRects([]);
      return undefined;
    }

    const updateRect = () => {
      const nextRects = resolveFocusGroups(step.id, stepConfig)
        .map((groupSelectors) => buildGroupRect(queryAll(groupSelectors)))
        .filter(Boolean);
      setFocusRects(nextRects);
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    const interval = window.setInterval(updateRect, 250);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
      window.clearInterval(interval);
    };
  }, [location.key, setupEnabled, step, stepConfig]);

  useEffect(() => {
    const onSaving = event => setSaving(Boolean(event.detail));
    window.addEventListener('setup-tour:saving', onSaving);
    return () => window.removeEventListener('setup-tour:saving', onSaving);
  }, []);

  const overlayStyles = useMemo(
    () =>
      focusRects.map((rect) => ({
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      })),
    [focusRects]
  );

  if (!setupEnabled || !step || !stepConfig) return null;

  const overlayMaskId = "setup-tour-overlay-mask";

  const leaveForLater = () => {
    updateSetupTourState({ active: false });
    dispatchSetupTourStatusChanged();
    navigate("/dashboard", { replace: true });
  };

  const goToStep = (nextStepId) => {
    if (!nextStepId) return;
    updateSetupTourState({
      active: true,
      currentStep: nextStepId,
      paymentsReviewed:
        step.id === "payments" ? true : Boolean(readSetupTourState()?.paymentsReviewed),
    });
    dispatchSetupTourStatusChanged();
    navigate(`/setup?step=${nextStepId}`, { replace: true });
  };

  const finishSetup = async () => {
    setActionPending(true);
    try {
      await setupAPI.updateStatus({ onboarding_dismissed: true });
      clearSetupTourState();
      dispatchSetupTourStatusChanged();
      toast.success("Setup abgeschlossen");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Setup konnte nicht abgeschlossen werden.");
    } finally {
      setActionPending(false);
    }
  };

  const skipCurrentStep = () => {
    if (step.id === "payments") {
      finishSetup();
      return;
    }
    goToStep(getNextSetupTourStepId(step.id));
  };

  const goBack = () => {
    if (stepIndex <= 0) return;
    goToStep(STEP_ORDER[stepIndex - 1]);
  };

  const finishBookingStep = async () => {
    try {
      await setupAPI.updateStatus({ booking_tested: true });
      goToStep("payments");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Buchungstest konnte nicht gespeichert werden.");
    } finally {
      setActionPending(false);
    }
  };

  const runPrimaryAction = async () => {
    const actionNode = queryFirst(stepConfig.actionSelector);
    setActionPending(true);

    const triggered = triggerAction(actionNode);
    const scrollTarget =
      focusRects[stepConfig.actionScrollGroup ?? 0] ||
      focusRects[focusRects.length - 1] ||
      null;
    if (!triggered && scrollTarget) {
      window.scrollTo({
        top: Math.max(0, scrollTarget.top + window.scrollY - 120),
        behavior: "smooth",
      });
    }

    if (step.id === "booking" && triggered) {
      window.setTimeout(() => {
        finishBookingStep();
      }, 250);
      return;
    }

    window.setTimeout(() => {
      setActionPending(false);
    }, 500);
  };

  return (
    <>
      {focusRects.length ? (
        <>
          <svg className="fixed inset-0 z-[90] pointer-events-none" width="100%" height="100%" aria-hidden="true">
            <defs>
              <mask id={overlayMaskId}>
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {focusRects.map((rect, index) => (
                  <rect
                    key={`mask-${index}`}
                    x={rect.left}
                    y={rect.top}
                    width={rect.width}
                    height={rect.height}
                    rx="28"
                    ry="28"
                    fill="black"
                  />
                ))}
              </mask>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.45)" mask={`url(#${overlayMaskId})`} />
          </svg>
          {overlayStyles.map((style, index) => (
            <div
              key={`outline-${index}`}
              className="fixed z-[91] rounded-[28px] border-2 border-white pointer-events-none"
              style={style}
            />
          ))}
        </>
      ) : null}

      <div className="fixed right-6 bottom-6 z-[95] w-[min(420px,calc(100vw-32px))] rounded-[28px] border border-[#E5E5EA] bg-white shadow-2xl">
        <div className="space-y-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black text-white">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-[0.14em] text-[#86868B]">
                  Erstlogin-Tutorial
                </p>
                <h3 className="text-xl font-semibold text-[#1D1D1F]">{resolveSetupTourText(step.title, vocab)}</h3>
                <p className="mt-1 text-sm text-[#86868B]">Schritt {stepIndex + 1} von 5</p>
              </div>
            </div>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#E5E5EA] text-[#86868B] hover:border-[#C7C7CC] hover:text-[#1D1D1F]"
              onClick={leaveForLater}
              disabled={actionPending || saving}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-2">
            <p className="text-sm leading-6 text-[#1D1D1F]">{resolveSetupTourText(step.description, vocab)}</p>
            <p className="text-sm leading-6 text-[#86868B]">{resolveSetupTourText(stepConfig.hint, vocab)}</p>
          </div>

          <div className="flex items-center gap-2">
            {STEP_ORDER.map((id, index) => (
              <div
                key={id}
                className={`transition-all ${
                  index <= stepIndex ? "h-2 flex-1 rounded-full bg-black" : "h-2 w-8 rounded-full bg-[#E5E5EA]"
                }`}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <Button type="button" className="btn-primary w-full" onClick={runPrimaryAction} disabled={actionPending || saving}>
              {saving ? "Wird gespeichert …" : actionPending
                ? "Wird geöffnet..."
                : step.id === "booking"
                  ? "Buchungsseite öffnen"
                  : resolveSetupTourText(stepConfig.primaryLabel, vocab)}
              {step.id === "booking" ? <Check className="ml-2 h-4 w-4" /> : <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>

            <Button type="button" variant="outline" className="w-full" onClick={skipCurrentStep} disabled={actionPending || saving}>
              {step.id === "payments" ? "Setup abschließen" : "Schritt überspringen"}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3">
            {stepIndex > 0 ? (
              <button
                type="button"
                onClick={goBack}
                disabled={actionPending || saving}
                className="inline-flex items-center gap-2 text-sm text-[#86868B] hover:text-[#1D1D1F]"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={leaveForLater}
              disabled={actionPending || saving}
              className="text-right text-sm text-[#86868B] hover:text-[#1D1D1F]"
            >
              Später fortsetzen
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
